import { ChevronDown, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function ActionTooltip({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="top"
          className="text-xs bg-popover text-popover-foreground border-border"
        >
          {title}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function Chip({
  children,
  onClear,
  tone = "info",
}: {
  children: React.ReactNode;
  onClear: () => void;
  tone?: "info" | "destructive";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium transition-all duration-150 ease-out animate-in fade-in slide-in-from-left-1",
        tone === "info"
          ? "border-transparent bg-muted text-foreground hover:bg-muted/80"
          : "bg-destructive/10 border-destructive/20 text-destructive hover:bg-destructive/20",
      )}
    >
      {children}
      <button
        type="button"
        onClick={onClear}
        className="rounded-full p-0.5 hover:bg-foreground/10 cursor-pointer text-muted-foreground hover:text-foreground"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

export function FilterGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: { id: string; label: string }[];
  selected: readonly string[];
  onToggle: (id: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        {label}
      </span>
      <div className="mt-0.5 flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            aria-pressed={selected.includes(opt.id)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-[11px] transition-colors cursor-pointer outline-none",
              selected.includes(opt.id)
                ? "border-transparent bg-muted text-foreground font-medium"
                : "border-border text-muted-foreground hover:border-foreground/20 hover:bg-muted/30 hover:text-foreground",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TagFilterSelect({
  options,
  selected,
  onToggle,
}: {
  options: { id: string; label: string }[];
  selected: readonly string[];
  onToggle: (id: string) => void;
}) {
  if (options.length === 0) return null;

  return (
    <div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        Tag
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Filter by tags"
            className="mt-0.5 h-7 w-full justify-between text-[11.5px] font-normal"
          >
            <span className="truncate">
              {selected.length > 0
                ? `${selected.length} tag${selected.length === 1 ? "" : "s"} selected`
                : "Select tags"}
            </span>
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search tags…"
              className="text-[11.5px]"
            />
            <CommandList>
              <CommandEmpty className="py-4 text-center text-[11.5px]">
                No tags found.
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected.includes(option.id);
                  return (
                    <CommandItem
                      key={option.id}
                      value={`${option.label} ${option.id}`}
                      className="gap-2 text-[11.5px]"
                      onSelect={() => onToggle(option.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="pointer-events-none"
                      />
                      <span className="truncate">{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {selected.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => selected.forEach(onToggle)}
                      className="justify-center text-center text-[11.5px]"
                    >
                      Clear tag filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
